"use client";
import { useEffect, useCallback, useState, useRef } from "react";
import type { MuteRequestPayload } from "@client/types/livekit.types";
import { Room, RoomEvent, Participant, ParticipantEvent, Track, TrackPublication } from "livekit-client";

/**
 * State for a single participant's mute status.
 */
export interface ParticipantMuteInfo {
  identity: string;
  sid: string;
  name: string;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  isMutedByHost: boolean;
}

interface UseParticipantMuteStateOptions {
  room: Room;
  onMutedByHost?: () => void;
  onUnmutedByHost?: () => void;
}

interface UseParticipantMuteStateReturn {
  participantMuteStates: Map<string, ParticipantMuteInfo>;
  getMuteState: (sid: string) => ParticipantMuteInfo | undefined;
  isAudioMuted: (sid: string) => boolean;
  isVideoMuted: (sid: string) => boolean;
  wasMutedByHost: boolean;
  clearMutedByHost: () => void;
}

/**
 * Real-time tracking of participant mute states.
 * Listens to LiveKit events for mute/unmute changes and updates UI.
 * Features:
 * - Tracks audio/video mute states for all participants.
 * - Handles host-initiated mutes.
 * - Optimized with refs.
 */
export const useParticipantMuteState = ({
  room,
  onMutedByHost,
  onUnmutedByHost
}: UseParticipantMuteStateOptions): UseParticipantMuteStateReturn => {
  const [participantMuteStates, setParticipantMuteStates] = useState<Map<string, ParticipantMuteInfo>>(new Map());
  const [wasMutedByHost, setWasMutedByHost] = useState(false);

  // Refs for callbacks to avoid re-creating effect
  const onMutedByHostRef = useRef(onMutedByHost);
  const onUnmutedByHostRef = useRef(onUnmutedByHost);
  useEffect(() => {
    onMutedByHostRef.current = onMutedByHost;
    onUnmutedByHostRef.current = onUnmutedByHost;
  }, [onMutedByHost, onUnmutedByHost]);

  /**
   * Get mute state for a participant from their tracks
   */
  const getParticipantMuteInfo = useCallback((participant: Participant): ParticipantMuteInfo => {
    const audioTracks = Array.from(participant.audioTrackPublications.values()) as TrackPublication[];
    const videoTracks = Array.from(participant.videoTrackPublications.values()).filter(
      (t) => t.source === Track.Source.Camera
    ) as TrackPublication[];

    // Audio is muted if no tracks or all tracks are muted
    const isAudioMuted = audioTracks.length === 0 || audioTracks.every((t) => t.isMuted);

    // Video is muted if no tracks or all tracks are muted
    const isVideoMuted = videoTracks.length === 0 || videoTracks.every((t) => t.isMuted);

    return {
      identity: participant.identity,
      sid: participant.sid,
      name: participant.name || participant.identity,
      isAudioMuted,
      isVideoMuted,
      isMutedByHost: false
    };
  }, []);

  /**
   * Update a single participant's mute state
   */
  const updateParticipantState = useCallback(
    (participant: Participant, isMutedByHost?: boolean) => {
      setParticipantMuteStates((prev) => {
        const info = getParticipantMuteInfo(participant);
        const existingInfo = prev.get(participant.sid);

        // Preserve isMutedByHost if not explicitly set
        if (existingInfo && isMutedByHost === undefined) {
          info.isMutedByHost = existingInfo.isMutedByHost;
        } else if (isMutedByHost !== undefined) {
          info.isMutedByHost = isMutedByHost;
        }

        // Optimization: Only update if state has changed
        if (
          existingInfo &&
          existingInfo.isAudioMuted === info.isAudioMuted &&
          existingInfo.isVideoMuted === info.isVideoMuted &&
          existingInfo.isMutedByHost === info.isMutedByHost &&
          existingInfo.name === info.name
        ) {
          return prev;
        }

        const newMap = new Map(prev);
        newMap.set(participant.sid, info);
        return newMap;
      });
    },
    [getParticipantMuteInfo]
  );

  /**
   * Remove a participant from the state
   */
  const removeParticipantState = useCallback((participant: Participant) => {
    setParticipantMuteStates((prev) => {
      const newMap = new Map(prev);
      newMap.delete(participant.sid);
      return newMap;
    });
  }, []);

  /**
   * Initialize state for all current participants
   */
  const initializeAllParticipants = useCallback(() => {
    if (!room) return;

    const newStates = new Map<string, ParticipantMuteInfo>();

    // Add local participant
    if (room.localParticipant) {
      newStates.set(room.localParticipant.sid, getParticipantMuteInfo(room.localParticipant));
    }

    // Add all remote participants
    room.remoteParticipants.forEach((participant) => {
      newStates.set(participant.sid, getParticipantMuteInfo(participant));
    });

    setParticipantMuteStates(newStates);
  }, [room, getParticipantMuteInfo]);

  /**
   * Handle mute request from host via data channel
   */
  const handleMuteRequest = useCallback((payload: MuteRequestPayload) => {
    if (payload.action === "mute") {
      setWasMutedByHost(true);
      onMutedByHostRef.current?.();
    } else if (payload.action === "unmute") {
      setWasMutedByHost(false);
      onUnmutedByHostRef.current?.();
    }
  }, []);

  /**
   * Clear the muted by host flag
   */
  const clearMutedByHost = useCallback(() => {
    setWasMutedByHost(false);
  }, []);

  /**
   * Main effect - set up event listeners for Room and Participant events.
   * Handles:
   * - Track mute/unmute
   * - Track publish/unpublish
   * - Participant connection/disconnection
   * - Data messages (mute requests)
   */
  useEffect(() => {
    if (!room) return;

    // Initialize state
    initializeAllParticipants();

    // Event handlers
    const handleParticipantUpdate = (participant: Participant) => {
      updateParticipantState(participant);
    };

    const handleTrackEvent = (_: TrackPublication, participant: Participant) => {
      updateParticipantState(participant);
    };

    const handleTrackSubscription = (_track: Track, _publication: TrackPublication, participant: Participant) => {
      updateParticipantState(participant);
    };

    const handleDataReceived = (payload: Uint8Array) => {
      try {
        const message = JSON.parse(new TextDecoder().decode(payload));
        if (message.type === "mute-request") {
          handleMuteRequest(message as MuteRequestPayload);
        }
      } catch {
        // Ignore non-JSON or non-mute messages
      }
    };

    // Handle reconnection - re-initialize all participants
    const handleReconnected = () => {
      initializeAllParticipants();
    };

    // Handle connection state change for fresh state on rejoin
    const handleConnectionStateChanged = () => {
      initializeAllParticipants();
    };

    // LOCAL PARTICIPANT HANDLERS
    // We need named functions to properly remove listeners for local participant
    const handleLocalUpdate = () => {
      if (room.localParticipant) updateParticipantState(room.localParticipant);
    };

    // Subscribe to ROOM events (Covers all remote participants)
    room.on(RoomEvent.TrackMuted, handleTrackEvent);
    room.on(RoomEvent.TrackUnmuted, handleTrackEvent);
    room.on(RoomEvent.TrackPublished, handleTrackEvent);
    room.on(RoomEvent.TrackUnpublished, handleTrackEvent);
    room.on(RoomEvent.TrackSubscribed, handleTrackSubscription);
    room.on(RoomEvent.TrackUnsubscribed, handleTrackSubscription);
    room.on(RoomEvent.ParticipantConnected, handleParticipantUpdate);
    room.on(RoomEvent.ParticipantDisconnected, removeParticipantState);
    room.on(RoomEvent.DataReceived, handleDataReceived);
    room.on(RoomEvent.Reconnected, handleReconnected);
    room.on(RoomEvent.Connected, handleConnectionStateChanged);

    // Subscribe to LOCAL participant events (Room events might not cover local actions)
    if (room.localParticipant) {
      room.localParticipant.on(ParticipantEvent.TrackMuted, handleLocalUpdate);
      room.localParticipant.on(ParticipantEvent.TrackUnmuted, handleLocalUpdate);
      room.localParticipant.on(ParticipantEvent.LocalTrackPublished, handleLocalUpdate);
      room.localParticipant.on(ParticipantEvent.LocalTrackUnpublished, handleLocalUpdate);
    }

    // Cleanup
    return () => {
      room.off(RoomEvent.TrackMuted, handleTrackEvent);
      room.off(RoomEvent.TrackUnmuted, handleTrackEvent);
      room.off(RoomEvent.TrackPublished, handleTrackEvent);
      room.off(RoomEvent.TrackUnpublished, handleTrackEvent);
      room.off(RoomEvent.TrackSubscribed, handleTrackSubscription);
      room.off(RoomEvent.TrackUnsubscribed, handleTrackSubscription);
      room.off(RoomEvent.ParticipantConnected, handleParticipantUpdate);
      room.off(RoomEvent.ParticipantDisconnected, removeParticipantState);
      room.off(RoomEvent.DataReceived, handleDataReceived);
      room.off(RoomEvent.Reconnected, handleReconnected);
      room.off(RoomEvent.Connected, handleConnectionStateChanged);

      // Cleanup local participant listeners
      if (room.localParticipant) {
        room.localParticipant.off(ParticipantEvent.TrackMuted, handleLocalUpdate);
        room.localParticipant.off(ParticipantEvent.TrackUnmuted, handleLocalUpdate);
        room.localParticipant.off(ParticipantEvent.LocalTrackPublished, handleLocalUpdate);
        room.localParticipant.off(ParticipantEvent.LocalTrackUnpublished, handleLocalUpdate);
      }
    };
  }, [room, initializeAllParticipants, updateParticipantState, removeParticipantState, handleMuteRequest]);

  /**
   * Helper functions for easy access to mute state
   */
  const getMuteState = useCallback(
    (sid: string): ParticipantMuteInfo | undefined => {
      return participantMuteStates.get(sid);
    },
    [participantMuteStates]
  );

  const isAudioMuted = useCallback(
    (sid: string): boolean => {
      return participantMuteStates.get(sid)?.isAudioMuted ?? true;
    },
    [participantMuteStates]
  );

  const isVideoMuted = useCallback(
    (sid: string): boolean => {
      return participantMuteStates.get(sid)?.isVideoMuted ?? true;
    },
    [participantMuteStates]
  );

  return {
    participantMuteStates,
    getMuteState,
    isAudioMuted,
    isVideoMuted,
    wasMutedByHost,
    clearMutedByHost
  };
};
